import { useEffect, useRef, useState } from 'react'
import useQuery from 'hooks/useQuery'

// Feedback de reunión 2026-08-19: durante los minutos que tarda el pipeline
// de resumen de IA (meet -> api) en devolver la evolución, el profesional no
// tenía ningún indicio de que algo se está procesando. Mismo criterio de
// poll que useHceSummaryApi.js (esa feature es más pesada — map-reduce
// sobre TODA la historia clínica — así que su cadencia es más laxa; acá el
// pipeline resuelve típicamente en 1-2 minutos).
const POLL_INTERVAL_MS = 5000

// Mismo bug-class ya conocido (useHceSummaryApi/Facturar.js): sin un tope de
// espera EN VIVO, un poll que sigue dando pending:true (o que sigue
// fallando) deja el indicador colgado para siempre. Un poco más largo que
// el TTL del lado del server (AI_SUMMARY_PENDING_TTL_S, ver
// api/src/ai-summary/pending.js) — así el servidor normalmente ya apagó el
// flag solo antes de que el cliente se dé por vencido.
//
// [Fase G, 2026-09-03] 12min alcanzaba cuando el pipeline corría sobre un
// clip único recién subido al cortar — con consultas largas (audio por
// tramos, STT en lotes de ~10min, ver meet/server.js) el pipeline completo
// puede tardar bastante más. Mismo horizonte que el TTL del server, ya
// subido a 60min en el mismo cambio.
export const AI_SUMMARY_PENDING_GIVE_UP_MS = 60 * 60 * 1000

/**
 * Indicador de "procesando" del resumen de videoconsulta con IA — GET
 * /teams/:teamID/ai-summary/consumers/:consumerID/pending vía useQuery
 * (reusa la autenticación Auth0 del resto de la app). Mismo criterio que
 * useHceSummaryApi: el prefijo /teams/:teamID lo agrega window._l solo,
 * leyendo la URL actual (config/window.js) — no hace falta pasarle teamID
 * a mano. A diferencia de useHceSummaryApi, no hay "generar"/"calificar":
 * este pipeline lo dispara meet automáticamente al terminar la
 * videoconsulta, drmanager solo observa.
 *
 * Card V3 (feedback Lucio, 2026-08-28): `outcome` es nuevo — antes el
 * cartel de "procesando" solo aparecía/desaparecía, sin distinguir "terminó
 * bien" de "terminó mal". Se captura apenas hay un fetch con un `status`
 * real (no null) en la respuesta (ver api/src/ai-summary/router.js) — tanto
 * en la transición pending:true→false como si YA estaba resuelto desde el
 * primer fetch (profesional entra a la ficha después de que el pipeline
 * terminó). `status:null` (corrida en curso sin resultado aún, O el
 * consumer nunca tuvo ninguna corrida) sigue polleando — puede arrancar o
 * resolver en cualquier momento desde otro lado (la videollamada). `null`
 * en el outcome expuesto si terminó bien (status:'saved', la evolución real
 * ya va a aparecer en la lista); `{status}` si terminó en cualquier otro
 * estado. El efecto de give-up por timeout (abajo) setea
 * `{status:'timed_out'}` si pasan los 60 minutos sin que llegue ningún
 * status real — pipeline genuinamente colgado, no solo "el status ya lo
 * sabemos y es malo".
 */
export function useAiSummaryPending({ consumerID } = {}) {
  const [pending, setPending] = useState(false)
  const [gaveUp, setGaveUp] = useState(false)
  const [outcome, setOutcome] = useState(null)
  const pollStartedAt = useRef(null)
  const wasPendingRef = useRef(false)

  const query = useQuery(
    window._l(`/ai-summary/consumers/${consumerID}/pending`).substr(1),
    {
      enabled: !!consumerID,
      // Bug real (QA, Juan Cruz, 2026-08-20): antes solo pollaba una vez
      // `pending` YA era true — pero el flag lo prende meet recién cuando
      // TERMINA la llamada, muy después del primer (y único) fetch al
      // montar este hook. Sin pollear desde el montaje, nunca se detectaba
      // la transición false->true: el profesional cerraba la ventana de la
      // llamada y no veía ningún indicio de procesamiento en "evoluciones"
      // hasta recargar la página a mano (un mount nuevo, con el fetch único
      // cayendo por casualidad después de que el pipeline ya había
      // terminado). Ahora pollea siempre hasta `gaveUp` — que cubre tanto
      // "nunca hubo nada que esperar" como "ya terminó de procesarse" (ver
      // los dos efectos de abajo).
      refetchInterval: gaveUp ? false : POLL_INTERVAL_MS,
    }
  )

  // Mismo motivo que useHceSummaryApi: no se puede leer el resultado de un
  // useQuery dentro de las opciones de ese mismo useQuery en el mismo
  // render — `pending` es un state separado, sincronizado acá.
  useEffect(() => {
    setPending(query.data?.pending === true)
  }, [query.data])

  useEffect(() => {
    // Corrida NUEVA (pending pasa de false a true) — esperar de cero, mismo
    // criterio que useHceSummaryApi (una corrida con id distinto resetea
    // gaveUp). Acá no hay un id de corrida — el booleano transitando
    // false->true ES la señal. outcome también se limpia — un cartel de
    // falla de la corrida ANTERIOR no debe seguir viéndose durante una
    // corrida nueva que ya está en curso.
    if (pending && !wasPendingRef.current) { setGaveUp(false); pollStartedAt.current = null; setOutcome(null) }
    // Terminó de procesarse (o YA estaba terminado desde el primer fetch —
    // bug real reportado por Juan Cruz, 2026-09-08): no queda nada más por
    // esperar — dejar de pollear hasta que una corrida nueva lo reactive
    // (arriba). El server SIEMPRE manda `status` en la respuesta (ver
    // api/src/ai-summary/router.js) — `null` tanto para "esta corrida sigue
    // en curso, sin resultado todavía" como para "este consumer nunca tuvo
    // ninguna corrida" (en AMBOS casos hay que seguir polleando: la corrida
    // puede arrancar o resolver en cualquier momento, desde otra pestaña con
    // la videollamada); cualquier string real ('saved', 'discarded_*', etc.)
    // es un status terminal — ahí sí no queda nada por esperar.
    //
    // ANTES exigía `wasPendingRef.current` (una transición true->false) —
    // eso asume que el hook siempre monta ANTES de que el pipeline termine.
    // Si el profesional entra a la ficha DESPUÉS de que el pipeline ya
    // resolvió (`pending` nunca fue true en la vida de ESTE hook), esa
    // condición nunca disparaba: seguía polleando cada 5s contra el backend
    // hasta el timeout de 60min, y encima mostraba 'timed_out' en vez del
    // status real ya conocido.
    if (!pending && query.data?.status) {
      setGaveUp(true)
      const status = query.data.status
      setOutcome(status !== 'saved' ? { status } : null)
    }
    wasPendingRef.current = pending
  }, [pending, query.data])

  useEffect(() => {
    if (gaveUp) { pollStartedAt.current = null; return }
    if (!pollStartedAt.current) pollStartedAt.current = Date.now()
    if (Date.now() - pollStartedAt.current > AI_SUMMARY_PENDING_GIVE_UP_MS) {
      setGaveUp(true)
      setOutcome({ status: 'timed_out' })
    }
    // `pending` no se lee en el cuerpo, pero tiene que quedar en las deps:
    // este efecto necesita re-evaluar en el MISMO render en que el otro
    // efecto resetea `pollStartedAt.current` (al detectar una transición de
    // `pending`) — si no, ese reset queda "invisible" hasta que algún OTRO
    // dep cambie más tarde, y en ese momento el efecto arranca el reloj
    // desde el `Date.now()` de ESE momento en vez del de la transición real
    // (bug real encontrado con un test: el timeout nunca disparaba).
  }, [pending, gaveUp, query.dataUpdatedAt, query.errorUpdatedAt])

  return { pending, gaveUp, outcome }
}

export default useAiSummaryPending
